/**
 * DepthParallax — back-compatible facade over the shared depth-engine.
 *
 * History: this file used to BE the implementation, and a near-identical copy
 * of it lived at public/js/slideshow/slideshow-parallax.js. Both drew the
 * picture twice — once whole, once alpha-masked by depth — and panned the two
 * copies apart. That construction cannot render a correct occlusion: the gap
 * that should open behind a near object gets filled with the same image
 * offset, so every high-contrast edge doubles. Feathering the mask only
 * smears the double.
 *
 * The public API below is unchanged so /audio-to-video needed no edits, but
 * the pixels now come from depth-engine's ray-marched renderer. Measured on
 * the synthetic scene in tests/playwright/depth-engine-geometry.mjs
 * (2026-08-06): mean error in the disocclusion band fell from 98.81 to 4.47,
 * and the near plane now moves by exactly the analytically correct amount.
 *
 * The old two-layer path survives as LEGACY below, used only where WebGL2 is
 * unavailable. A worse picture is a fair trade for a picture; no picture is
 * not. Load via @moduleGraph('js/depth-parallax.js') — it has relative
 * imports now.
 */

import DepthEngine from './depth-engine/index.js';

var DEFAULTS = {
    inputSize: 384,
    fgThreshold: 0.5,   // LEGACY path only
    feather: 0.12,      // LEGACY path only
    overscale: 1.12,
    depthScale: 0.05,   // max displacement = depthFrac * depthScale * min(w,h)
};

// One renderer serves every handle: uploading a new colour + depth texture is
// far cheaper than standing up another WebGL2 context, and browsers cap the
// number of live contexts (~16) — a slideshow with 20 slides would silently
// lose the earliest ones.
var _renderer = null;
var _glCanvas = null;
var _boundHandle = null;

function renderer() {
    if (_renderer && !_renderer.isLost()) return _renderer;
    if (!DepthEngine.isSupported()) return null;
    _glCanvas = document.createElement('canvas');
    _renderer = DepthEngine.createRenderer(_glCanvas);
    _boundHandle = null;
    return _renderer;
}

export function isRuntimeReady() { return DepthEngine.isRuntimeReady(); }

export function ensureRuntime(coreUrl, coreFallbackUrl) {
    return DepthEngine.ensureRuntime(coreUrl, coreFallbackUrl);
}

/**
 * analyze(imageEl, opts, onProgress) -> handle
 * handle = { depth, dims:{w,h}, srcW, srcH, image, fg?  }
 * `fg` only exists on the legacy path; nothing outside this file reads it.
 */
export function analyze(imageEl, opts, onProgress) {
    opts = opts || {};
    if (!imageEl || !(imageEl.naturalWidth || imageEl.width)) {
        return Promise.reject(new Error('analyze: image not ready'));
    }
    if (!opts.modelUrls || !opts.modelUrls.length) {
        return Promise.reject(new Error('analyze: modelUrls required'));
    }
    if (!isRuntimeReady()) {
        return Promise.reject(new Error('analyze: depth runtime not ready (call ensureRuntime first)'));
    }

    var srcW = imageEl.naturalWidth || imageEl.width;
    var srcH = imageEl.naturalHeight || imageEl.height;

    return DepthEngine.analyze(imageEl, {
        modelUrls: opts.modelUrls,
        inputSize: opts.inputSize || DEFAULTS.inputSize,
        onProgress: onProgress,
    }).then(function (map) {
        var handle = {
            map: map,
            depth: map.data,           // legacy field name, still read by nobody but kept
            dims: { w: map.w, h: map.h },
            srcW: srcW, srcH: srcH,
            image: imageEl,
            bg: imageEl,               // legacy field
            fg: null,
        };
        if (!renderer()) handle.fg = buildLegacyForeground(imageEl, map, opts);
        return handle;
    });
}

/** draw(ctx, handle, w, h, params) — one frame, deterministic in params. */
export function draw(ctx, handle, w, h, params) {
    if (!handle) return false;
    params = params || {};
    var r = renderer();

    if (!r) return legacyDraw(ctx, handle, w, h, params);

    if (_boundHandle !== handle) {
        if (!r.setColor(handle.image)) return false;
        r.setDepth(handle.map, { refine: true });
        _boundHandle = handle;
    }
    r.resize(w, h);

    var depthFrac = params.depthFrac != null ? clamp(params.depthFrac, 0, 1) : 1;
    var ok = r.render({
        camX: params.camX || 0,
        camY: params.camY || 0,
        camZ: 0,
        // Match the old visual scale: the legacy renderer's total relative
        // shift was depthFrac * 0.05 * min(w,h). Keeping the number means
        // /audio-to-video's intensity slider still means what it meant.
        strength: depthFrac * DEFAULTS.depthScale * Math.min(w, h) / w,
        focus: params.focus != null ? params.focus : 0.5,
        overscale: params.overscale || DEFAULTS.overscale,
        steps: 40,
        mode: 'mono',
        dof: params.dof || 0,
        invert: params.invert || 0,
    });
    if (!ok) return false;
    ctx.drawImage(_glCanvas, 0, 0, w, h);
    return true;
}

/** Unchanged: /audio-to-video passes absolute seconds and expects this shape. */
export function cameraOrbit(timeSec, opts) {
    opts = opts || {};
    var px = opts.periodX || 6, py = opts.periodY || 9, TWO_PI = Math.PI * 2;
    return {
        x: Math.sin((timeSec / px) * TWO_PI),
        y: Math.sin((timeSec / py) * TWO_PI + Math.PI / 2) * (opts.yScale != null ? opts.yScale : 0.45),
    };
}

export function release(handle) {
    if (!handle) return;
    if (_boundHandle === handle) _boundHandle = null;
    if (handle.fg) { handle.fg.width = 0; handle.fg.height = 0; handle.fg = null; }
}

/* ───────────────────────── LEGACY two-layer path ─────────────────────────
   Only reached when WebGL2 is missing. Kept verbatim in behaviour so a
   device that lands here sees exactly what it saw before this migration. */

function buildLegacyForeground(imageEl, map, opts) {
    var fgThreshold = opts.fgThreshold != null ? opts.fgThreshold : DEFAULTS.fgThreshold;
    var feather = opts.feather != null ? opts.feather : DEFAULTS.feather;
    var srcW = imageEl.naturalWidth || imageEl.width;
    var srcH = imageEl.naturalHeight || imageEl.height;
    var fg = document.createElement('canvas');
    fg.width = srcW; fg.height = srcH;
    var c = fg.getContext('2d');
    c.drawImage(imageEl, 0, 0, srcW, srcH);
    var img = c.getImageData(0, 0, srcW, srcH);
    var data = img.data, depth = map.data, dW = map.w, dH = map.h;
    var lo = fgThreshold - feather, span = 2 * feather;
    for (var y = 0; y < srcH; y++) {
        var fy = (srcH > 1 ? (y / (srcH - 1)) : 0) * (dH - 1);
        var y0 = Math.floor(fy), y1 = Math.min(dH - 1, y0 + 1), wy = fy - y0;
        for (var x = 0; x < srcW; x++) {
            var fx = (srcW > 1 ? (x / (srcW - 1)) : 0) * (dW - 1);
            var x0 = Math.floor(fx), x1 = Math.min(dW - 1, x0 + 1), wx = fx - x0;
            var d = depth[y0 * dW + x0] * (1 - wx) * (1 - wy) +
                    depth[y0 * dW + x1] * wx * (1 - wy) +
                    depth[y1 * dW + x0] * (1 - wx) * wy +
                    depth[y1 * dW + x1] * wx * wy;
            var a = (d - lo) / span;
            data[(y * srcW + x) * 4 + 3] = ((a < 0 ? 0 : (a > 1 ? 1 : a)) * 255) | 0;
        }
    }
    c.putImageData(img, 0, 0);
    return fg;
}

function coverRect(iw, ih, w, h, overscale, shiftX, shiftY) {
    var base = Math.max(w / iw, h / ih) * overscale;
    var dw = iw * base, dh = ih * base;
    var dx = (w - dw) / 2 + shiftX;
    var dy = (h - dh) / 2 + shiftY;
    if (dx > 0) dx = 0; else if (dx < w - dw) dx = w - dw;
    if (dy > 0) dy = 0; else if (dy < h - dh) dy = h - dh;
    return { dx: dx, dy: dy, dw: dw, dh: dh };
}

function legacyDraw(ctx, handle, w, h, params) {
    if (!handle.fg || !handle.image) return false;
    var overscale = params.overscale || DEFAULTS.overscale;
    var depthFrac = params.depthFrac != null ? clamp(params.depthFrac, 0, 1) : 1;
    var maxShift = depthFrac * DEFAULTS.depthScale * Math.min(w, h);
    var sx = (params.camX || 0) * maxShift * 0.5;
    var sy = (params.camY || 0) * maxShift * 0.5;
    var rb = coverRect(handle.srcW, handle.srcH, w, h, overscale, -sx, -sy);
    ctx.drawImage(handle.image, rb.dx, rb.dy, rb.dw, rb.dh);
    var rf = coverRect(handle.srcW, handle.srcH, w, h, overscale, sx, sy);
    ctx.drawImage(handle.fg, rf.dx, rf.dy, rf.dw, rf.dh);
    return true;
}

function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

window.DepthParallax = {
    isRuntimeReady: isRuntimeReady,
    ensureRuntime: ensureRuntime,
    analyze: analyze,
    draw: draw,
    cameraOrbit: cameraOrbit,
    release: release,
};
